export interface GradingScaleBorder {
  label: string;
  minPercentage: number; // 0–100, array sorted descending; last entry is the lowest grade (typically minPercentage: 0)
}

export interface GradingScale {
  type: 'german-1-6' | 'drittel' | 'a-f' | 'gpa-4' | 'notenpunkte' | 'numeric-10' | 'percentage' | 'custom';
  borders: GradingScaleBorder[];
}

export const PREDEFINED_GRADING_SCALES: Record<string, GradingScale> = {
  'german-1-6': {
    type: 'german-1-6',
    borders: [
      { label: '1', minPercentage: 93 },
      { label: '1-', minPercentage: 89 },

      { label: '2+', minPercentage: 85 },
      { label: '2', minPercentage: 81 },
      { label: '2-', minPercentage: 76 },

      { label: '3+', minPercentage: 71 },
      { label: '3', minPercentage: 66 },
      { label: '3-', minPercentage: 60 },

      { label: '4+', minPercentage: 55 },
      { label: '4', minPercentage: 50 },
      { label: '4-', minPercentage: 45 },

      { label: '5+', minPercentage: 39 },
      { label: '5', minPercentage: 33 },
      { label: '5-', minPercentage: 26 },

      { label: '6+', minPercentage: 15 },
      { label: '6', minPercentage: 0 },
    ],
  },
  drittel: {
    type: 'drittel',
    borders: [
      { label: '1.0', minPercentage: 95 },
      { label: '1.3', minPercentage: 90 },
      { label: '1.7', minPercentage: 85 },
      { label: '2.0', minPercentage: 80 },
      { label: '2.3', minPercentage: 75 },
      { label: '2.7', minPercentage: 70 },
      { label: '3.0', minPercentage: 65 },
      { label: '3.3', minPercentage: 60 },
      { label: '3.7', minPercentage: 55 },
      { label: '4.0', minPercentage: 50 },
      { label: '5.0', minPercentage: 0 },
    ],
  },
  'a-f': {
    type: 'a-f',
    borders: [
      { label: 'A', minPercentage: 90 },
      { label: 'B', minPercentage: 80 },
      { label: 'C', minPercentage: 70 },
      { label: 'D', minPercentage: 60 },
      { label: 'F', minPercentage: 0 },
    ],
  },
  'gpa-4': {
    type: 'gpa-4',
    borders: [
      { label: '4.0', minPercentage: 90 },
      { label: '3.0', minPercentage: 80 },
      { label: '2.0', minPercentage: 70 },
      { label: '1.0', minPercentage: 60 },
      { label: '0.0', minPercentage: 0 },
    ],
  },
  notenpunkte: {
    type: 'notenpunkte',
    borders: [
      { label: '15', minPercentage: 95 },
      { label: '14', minPercentage: 90 },
      { label: '13', minPercentage: 85 },
      { label: '12', minPercentage: 80 },
      { label: '11', minPercentage: 75 },
      { label: '10', minPercentage: 70 },
      { label: '9', minPercentage: 65 },
      { label: '8', minPercentage: 60 },
      { label: '7', minPercentage: 55 },
      { label: '6', minPercentage: 50 },
      { label: '5', minPercentage: 45 },
      { label: '4', minPercentage: 40 },
      { label: '3', minPercentage: 33 },
      { label: '2', minPercentage: 27 },
      { label: '1', minPercentage: 20 },
      { label: '0', minPercentage: 0 },
    ],
  },
  'numeric-10': {
    type: 'numeric-10',
    borders: [
      { label: '10', minPercentage: 91 },
      { label: '9', minPercentage: 81 },
      { label: '8', minPercentage: 71 },
      { label: '7', minPercentage: 61 },
      { label: '6', minPercentage: 51 },
      { label: '5', minPercentage: 41 },
      { label: '4', minPercentage: 31 },
      { label: '3', minPercentage: 21 },
      { label: '2', minPercentage: 11 },
      { label: '1', minPercentage: 0 },
    ],
  },
  percentage: {
    type: 'percentage',
    borders: [{ label: 'percentage', minPercentage: 0 }],
  },
};

export const DEFAULT_GRADING_SCALE: GradingScale = PREDEFINED_GRADING_SCALES['german-1-6']!;

export function gradeFromPercentage(percentage: number, scale: GradingScale): string {
  if (scale.type === 'percentage') {
    return `${Math.round(percentage)}%`;
  }
  for (const border of scale.borders) {
    if (percentage >= border.minPercentage) return border.label;
  }
  // Fallback: return the last border's label (lowest grade)
  return scale.borders[scale.borders.length - 1]?.label ?? '?';
}

export function isNumericGradeScale(scale: GradingScale): boolean {
  if (scale.type === 'percentage' || scale.type === 'a-f') return false;
  // Custom scales: check if all labels parse to finite numbers
  return scale.borders.every(b => Number.isFinite(parseFloat(b.label)));
}

export function averageNumericGrade(grades: string[], scale: GradingScale): number | null {
  if (!isNumericGradeScale(scale) || grades.length === 0) return null;
  const nums = grades.map(g => parseFloat(g)).filter(n => Number.isFinite(n));
  if (nums.length === 0) return null;
  return nums.reduce((s, n) => s + n, 0) / nums.length;
}

export function parseGradingScale(json: string | null | undefined): GradingScale {
  if (!json) return DEFAULT_GRADING_SCALE;
  try {
    const parsed = JSON.parse(json) as GradingScale;
    if (!parsed?.type || !Array.isArray(parsed.borders)) return DEFAULT_GRADING_SCALE;
    return parsed;
  } catch {
    return DEFAULT_GRADING_SCALE;
  }
}
